/**
 * Search Slice
 *
 * Manages destination search state
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { DestinationSuggestion } from '@contracts';
import { searchDestinationsThunk } from '../thunks/searchThunks';

// ============================================
// State Types
// ============================================

export interface SearchState {
  suggestions: DestinationSuggestion[];
  isLoading: boolean;
  error: string | null;
  lastQuery: string;
}

// ============================================
// Initial State
// ============================================

export const initialSearchState: SearchState = {
  suggestions: [],
  isLoading: false,
  error: null,
  lastQuery: '',
};

// ============================================
// Slice
// ============================================

const searchSlice = createSlice({
  name: 'onboarding/search',
  initialState: initialSearchState,
  reducers: {
    setSearchLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    clearSuggestions: (state) => {
      state.suggestions = [];
      state.lastQuery = '';
    },

    resetSearchState: () => initialSearchState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDestinationsThunk.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.lastQuery = action.meta.arg;
      })
      .addCase(searchDestinationsThunk.fulfilled, (state, action) => {
        state.suggestions = action.payload;
        state.isLoading = false;
      })
      .addCase(searchDestinationsThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Unknown error';
      });
  },
});

// ============================================
// Actions
// ============================================

export const { setSearchLoading, clearSuggestions, resetSearchState } = searchSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const searchReducer = searchSlice.reducer;
